import {
  errorCodeForClose,
  ERROR_CODES,
  PROTOCOL_VERSION,
  ROOM_NAMES,
  SERVER_MESSAGES,
  type AnnouncementEvent,
  type ChatEvent,
  type ChatRejectedEvent,
  type DirectMessageEvent,
  type DmRejectedEvent,
  type InteractRejectedEvent,
  type JoinOptions,
  type MapScheduledEvent,
  type ObjectEvent,
  type QueueJoinResponse,
  type QueuePositionEvent,
  type QueueTicketEvent,
  type EmoteEvent,
  type TypingEvent,
} from '@veriverse/shared';
import { Client, type Room } from 'colyseus.js';
import { clientConfig } from '../config.js';
import { useSessionStore } from '../state/session-store.js';
import { api, ApiError, getAccessToken } from './api.js';
import { handleIncomingDm, loadConversations } from './dm.js';
import { objectEvents } from './object-event-handlers.js';
import { createSseParser } from './sse.js';
import { emoteEvents } from '../game/emotes.js';
import { typingEvents } from './typing-indicator.js';

const KNOWN_ERRORS = new Set<string>(Object.values(ERROR_CODES));

/** How long we keep trying to resume a dropped connection (the server holds the seat for 60 s). */
const RECONNECT_WINDOW_MS = 55_000;
const RETRY_DELAY_MS = 2_000;
/** Close codes that mean "we left on purpose": 1000 (normal) and Colyseus' consented 4000. */
const INTENTIONAL_CLOSE = new Set([1000, 4000]);

/** Maps a join failure to an i18n key when it is one of ours, otherwise keeps the raw message. */
export function describeJoinError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  return KNOWN_ERRORS.has(message) ? `errors.${message}` : message;
}

let activeRoom: Room | undefined;
let queueAbort: AbortController | undefined;
/** The office picked on the title screen; kept across queue waits and ticket retries. */
let chosenOffice: string | undefined;

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });

/**
 * Enters the office: asks the queue for a seat (spec §6.2). With a free seat the server answers
 * with a ticket at once; otherwise we wait in line and are told our position until it is our turn.
 */
export async function enterOffice(office?: string, attempt = 0): Promise<void> {
  if (attempt === 0) chosenOffice = office;
  const store = useSessionStore.getState();
  store.setConnecting();
  try {
    const joined = await api<QueueJoinResponse>('/api/queue/join', { method: 'POST' });
    if (joined.state === 'ticket') {
      await joinWithTicket(joined.ticket, attempt);
      return;
    }
    store.setQueued({
      position: joined.position,
      etaSeconds: joined.etaSeconds,
      queued: joined.queued,
    });
    await waitInQueue(attempt);
  } catch (error) {
    store.setError(describeJoinError(error));
  }
}

/** Leaves the queue (the "Cancel" button). */
export function cancelQueue(): void {
  queueAbort?.abort();
  queueAbort = undefined;
  void api('/api/queue', { method: 'DELETE' }).catch(() => undefined);
  useSessionStore.getState().setIdle();
}

async function waitInQueue(attempt: number): Promise<void> {
  queueAbort?.abort();
  const abort = new AbortController();
  queueAbort = abort;

  while (!abort.signal.aborted) {
    try {
      const outcome = await readQueueStream(abort.signal);
      if (abort.signal.aborted) return;
      if (outcome.kind === 'ticket') {
        queueAbort = undefined;
        await joinWithTicket(outcome.ticket, attempt);
        return;
      }
      // The server forgot us (our heartbeat lapsed, e.g. after a long network outage): get back in line.
      const again = await api<QueueJoinResponse>('/api/queue/join', { method: 'POST' });
      if (again.state === 'ticket') {
        queueAbort = undefined;
        await joinWithTicket(again.ticket, attempt);
        return;
      }
      useSessionStore.getState().setQueued(again);
    } catch (error) {
      if (abort.signal.aborted) return;
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        useSessionStore.getState().setError('errors.UNAUTHORIZED');
        return;
      }
      await sleep(RETRY_DELAY_MS, abort.signal); // transient: the stream dropped, try again
    }
  }
}

type QueueOutcome = { kind: 'ticket'; ticket: string } | { kind: 'none' };

/** Reads `/api/queue/stream` until a ticket arrives, the server says we are not queued, or it drops. */
async function readQueueStream(signal: AbortSignal): Promise<QueueOutcome> {
  const token = await getAccessToken();
  if (!token) throw new ApiError(401, 'unauthorized', 'Not signed in');
  const res = await fetch(`${clientConfig.serverUrl}/api/queue/stream`, {
    headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
    signal,
  });
  if (!res.ok || !res.body) throw new ApiError(res.status, 'error', res.statusText);

  let outcome: QueueOutcome | undefined;
  const feed = createSseParser(({ event, data }) => {
    if (event === 'position') {
      useSessionStore.getState().setQueued(JSON.parse(data) as QueuePositionEvent);
    } else if (event === 'ticket') {
      outcome = { kind: 'ticket', ticket: (JSON.parse(data) as QueueTicketEvent).ticket };
    } else if (event === 'none') {
      outcome = { kind: 'none' };
    }
  });

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  try {
    while (!outcome) {
      const { done, value } = await reader.read();
      if (done) break;
      feed(decoder.decode(value, { stream: true }));
    }
  } finally {
    void reader.cancel().catch(() => undefined);
  }
  if (!outcome) throw new Error('Queue stream closed');
  return outcome;
}

async function joinWithTicket(ticket: string, attempt: number): Promise<void> {
  const store = useSessionStore.getState();
  try {
    const token = await getAccessToken();
    if (!token) {
      store.setSignedOut();
      return;
    }
    const client = new Client(clientConfig.serverUrl);
    const options: JoinOptions = {
      protocolVersion: PROTOCOL_VERSION,
      token,
      ticket,
      ...(chosenOffice ? { office: chosenOffice } : {}),
    };
    const room = await client.joinOrCreate(ROOM_NAMES.office, options);
    activeRoom = room;
    attachRoom(room);
    store.setConnected(room);
    void loadConversations();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    // The ticket expired or was used already: queue again, but do not loop forever.
    if (message === ERROR_CODES.ticketInvalid && attempt < 2) {
      await enterOffice(chosenOffice, attempt + 1);
      return;
    }
    store.setError(describeJoinError(error));
  }
}

/** Wires one room connection (also used again after a successful reconnect). */
function attachRoom(room: Room): void {
  room.onMessage(SERVER_MESSAGES.chatHistory, (history: ChatEvent[]) => {
    useSessionStore.getState().setChatHistory(history);
  });
  room.onMessage(SERVER_MESSAGES.chat, (event: ChatEvent) => {
    useSessionStore.getState().addChat(event);
  });
  room.onMessage(SERVER_MESSAGES.announcement, (event: AnnouncementEvent) => {
    useSessionStore.getState().setAnnouncement(event);
  });
  room.onMessage(SERVER_MESSAGES.typing, (event: TypingEvent) => {
    typingEvents.emit(event);
  });
  room.onMessage(SERVER_MESSAGES.emote, (event: EmoteEvent) => {
    emoteEvents.emit(event);
  });
  room.onMessage(SERVER_MESSAGES.chatRejected, (event: ChatRejectedEvent) => {
    useSessionStore.getState().setChatNotice(event.reason);
  });
  room.onMessage(SERVER_MESSAGES.mapScheduled, (event: MapScheduledEvent) => {
    useSessionStore.getState().setMapScheduled(event);
  });
  room.onMessage(SERVER_MESSAGES.mapChanged, () => {
    useSessionStore.getState().mapChanged();
  });
  room.onMessage(SERVER_MESSAGES.dm, (event: DirectMessageEvent) => {
    handleIncomingDm(event);
  });
  room.onMessage(SERVER_MESSAGES.dmRejected, (event: DmRejectedEvent) => {
    useSessionStore.getState().setDmNotice(event.reason);
  });
  room.onMessage(SERVER_MESSAGES.objectEvent, (event: ObjectEvent) => {
    objectEvents.dispatch(event);
  });
  room.onMessage(SERVER_MESSAGES.interactRejected, (event: InteractRejectedEvent) => {
    useSessionStore.getState().setInteractionNotice(event.reason);
  });
  room.onLeave((code) => void handleLeave(room, code));
  room.onError((code, message) => {
    useSessionStore.getState().setError(message ?? `error:${code}`);
  });
}

async function handleLeave(room: Room, code: number): Promise<void> {
  if (activeRoom !== room) return; // we left on purpose, or a newer connection replaced this one
  const known = errorCodeForClose(code);
  if (known || INTENTIONAL_CLOSE.has(code)) {
    activeRoom = undefined;
    useSessionStore.getState().setDisconnected(known ? `errors.${known}` : undefined);
    return;
  }
  await resume(room);
}

/**
 * The connection dropped without the server asking us to leave (Wi-Fi, sleeping laptop). The
 * server keeps our seat and avatar for a minute; we try to slip back in with the reconnection token.
 */
async function resume(lost: Room): Promise<void> {
  const store = useSessionStore.getState();
  store.setReconnecting();
  const token = lost.reconnectionToken;
  const deadline = Date.now() + RECONNECT_WINDOW_MS;

  while (Date.now() < deadline && activeRoom === lost) {
    try {
      const room = await new Client(clientConfig.serverUrl).reconnect(token);
      if (activeRoom !== lost) {
        void room.leave(true); // the user left while we were reconnecting
        return;
      }
      activeRoom = room;
      attachRoom(room);
      useSessionStore.getState().setConnected(room);
      return;
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (KNOWN_ERRORS.has(message)) break; // the server refused us on purpose
      await sleep(RETRY_DELAY_MS);
    }
  }
  if (activeRoom === lost) {
    activeRoom = undefined;
    useSessionStore.getState().setDisconnected('errors.CONNECTION_LOST');
  }
}

/** Leaves the office voluntarily (sign-out, or going back to the lobby). */
export function leaveOffice(): void {
  queueAbort?.abort();
  queueAbort = undefined;
  const room = activeRoom;
  activeRoom = undefined;
  if (room) void room.leave(true);
  useSessionStore.getState().setDisconnected(undefined);
}
